import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Copy, Check, Users, Wallet, Tag, RefreshCw, Share2, MessageCircle, Mail, MessageSquare, Send } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

const Promotions: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applyMsg, setApplyMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/referrals/my-code?context=customer`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copy = () => {
    if (!data?.code) return;
    navigator.clipboard?.writeText(data.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const APP_URL = 'https://play.google.com/store/apps/details?id=com.safaricab.app&pcampaignid=web_share';
  const shareText = () => `Join me on SafariCab! Use my code ${data?.code || ''} when you sign up. Download the app: ${APP_URL}`;

  // Native OS share sheet (shows every installed app: WhatsApp, Gmail, etc.).
  // Prefer the Capacitor Share plugin inside the app - it reliably opens the
  // native sheet where the web navigator.share API often doesn't.
  const share = async () => {
    const text = shareText();
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import('@capacitor/share');
        await Share.share({ title: 'SafariCab', text, dialogTitle: 'Share your invite code' });
        return;
      }
    } catch {}
    if ((navigator as any).share) {
      try { await (navigator as any).share({ title: 'SafariCab', text }); return; } catch {}
    }
    navigator.clipboard?.writeText(text);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // Direct deep-links to a specific app (fallback for webviews where the native
  // share sheet isn't available, and for users who want a one-tap target).
  const shareVia = (target: 'whatsapp' | 'sms' | 'email' | 'telegram') => {
    const text = shareText();
    const enc = encodeURIComponent(text);
    const urlEnc = encodeURIComponent(APP_URL);
    const links: Record<typeof target, string> = {
      whatsapp: `https://wa.me/?text=${enc}`,
      sms: `sms:?body=${enc}`,
      email: `mailto:?subject=${encodeURIComponent('Join me on SafariCab')}&body=${enc}`,
      telegram: `https://t.me/share/url?url=${urlEnc}&text=${encodeURIComponent(`Join me on SafariCab! Use my code ${data?.code || ''} when you sign up.`)}`,
    };
    const url = links[target];
    if (target === 'sms' || target === 'email') window.location.href = url;
    else window.open(url, '_blank');
  };

  const shareTargets = [
    { id: 'whatsapp' as const, label: 'WhatsApp', icon: MessageCircle, color: 'text-[#25D366]' },
    { id: 'sms' as const, label: 'SMS', icon: MessageSquare, color: 'text-blue-400' },
    { id: 'email' as const, label: 'Email', icon: Mail, color: 'text-red-400' },
    { id: 'telegram' as const, label: 'Telegram', icon: Send, color: 'text-[#229ED9]' },
  ];

  const submitApply = async () => {
    if (!applyCode.trim()) return;
    setApplyMsg('');
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/referrals/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: applyCode.trim(), context: 'customer' }),
      });
      const d = await res.json();
      if (res.ok) { setApplyMsg('✓ Code applied! Your inviter earns once you complete your trips.'); setApplyCode(''); }
      else setApplyMsg(d.error || 'Could not apply code');
    } catch { setApplyMsg('Failed to apply code'); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-white font-bold text-xl flex items-center gap-2"><Gift className="w-5 h-5 text-safaricab-gold" /> Invite & Earn</h2>
        <button onClick={load} className="text-white/40 hover:text-white"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Reward explainer */}
          <div className="bg-safaricab-gold/10 border border-safaricab-gold/30 rounded-2xl p-5 mb-5 text-center">
            <p className="text-white/60 text-sm mb-1">Earn for every friend you invite</p>
            <p className="text-safaricab-gold font-bold text-3xl">KES {(data?.rewardPerInvite || 50).toLocaleString()}</p>
            <p className="text-white/40 text-xs mt-1">
              Paid once they complete {data?.tripsRequired || 1} successful trip{(data?.tripsRequired || 1) > 1 ? 's' : ''}
            </p>
          </div>

          {/* My code */}
          <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">Your invite code</p>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 bg-safaricab-card border border-white/10 rounded-xl py-3 px-4 text-white font-mono font-bold text-lg text-center tracking-widest">
              {data?.code || '-'}
            </div>
            <button onClick={copy} className="bg-safaricab-card border border-white/10 rounded-xl p-3 text-safaricab-gold">
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <button onClick={share} className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl mb-3 flex items-center justify-center gap-2">
            <Share2 className="w-5 h-5" /> Share My Code
          </button>
          {/* Direct share to a specific app */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {shareTargets.map(({ id, label, icon: Icon, color }) => (
              <button key={id} onClick={() => shareVia(id)} aria-label={`Share via ${label}`}
                className="flex flex-col items-center gap-1 bg-safaricab-card border border-white/10 rounded-xl py-2.5 hover:border-safaricab-gold/40">
                <Icon className={`w-5 h-5 ${color}`} />
                <span className="text-white/50 text-[9px]">{label}</span>
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: Users, label: 'Invited', value: data?.invited || 0 },
              { icon: Wallet, label: 'Earned', value: `KES ${(data?.earned || 0).toLocaleString()}` },
              { icon: Gift, label: 'Pending', value: `KES ${(data?.pending || 0).toLocaleString()}` },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-safaricab-card rounded-xl p-3 border border-white/10 text-center">
                <Icon className="w-5 h-5 text-safaricab-gold mx-auto mb-1.5" />
                <p className="text-white font-bold text-sm">{value}</p>
                <p className="text-white/40 text-[10px]">{label}</p>
              </div>
            ))}
          </div>

          {data?.eligible > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-6 text-center">
              <p className="text-green-400 text-sm font-medium">KES {(data.eligible).toLocaleString()} ready - SafariCab will pay it to your registered phone.</p>
            </div>
          )}

          {/* Apply a code */}
          <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">Have an invite code?</p>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input value={applyCode} onChange={e => { setApplyCode(e.target.value.toUpperCase()); setApplyMsg(''); }}
                placeholder="Enter code" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white outline-none focus:border-safaricab-gold/50" />
            </div>
            <button onClick={submitApply} className="bg-white/10 text-white font-medium px-4 py-3 rounded-xl text-sm">Apply</button>
          </div>
          {applyMsg && <p className={`text-xs ${applyMsg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{applyMsg}</p>}
        </>
      )}
    </motion.div>
  );
};

export default Promotions;
